import * as Sentry from "@sentry/react";
import { useState, type ReactNode } from "react";
import { ErrorScreen, type ErrorScreenAction } from "./error-screen";

export interface ErrorBoundaryLabels {
  title: string;
  description: string;
  /** "Event id" - shown with the id the SDK assigned, so a person can quote it to support. */
  eventId: string;
  reload: string;
}

/**
 * The last boundary above the router: catches what the providers throw. `labels` is a function,
 * evaluated when the fallback renders, so the sentence comes out in the language the application runs
 * in. Without a DSN the SDK is not initialised and the boundary still catches; the event id is then absent.
 */
export function ErrorBoundary({ children, labels }: { children: ReactNode; labels: () => ErrorBoundaryLabels }) {
  return (
    <Sentry.ErrorBoundary
      fallback={({ eventId, resetError }) => (
        <ErrorScreen
          eventId={eventId}
          labels={labels()}
          onReload={() => {
            resetError();
            window.location.reload();
          }}
        />
      )}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}

/**
 * The same screen for TanStack Router's `defaultErrorComponent`. The router wraps every route in its own
 * boundary, so a screen that throws never reaches {@link ErrorBoundary} - it reaches this, which reports
 * the error once (in the state initialiser, so a re-render of the fallback does not report it again) and
 * draws the same thing. Wire it in `createRouter({ defaultErrorComponent })`.
 */
export function RouteErrorScreen({
  error,
  labels,
  report,
}: {
  error: unknown;
  labels: () => ErrorBoundaryLabels;
  /** Given the event id, so a report can carry it (EP-53). */
  report?: (eventId: string | undefined) => ErrorScreenAction | undefined;
}) {
  const [eventId] = useState(() => (Sentry.isInitialized() ? Sentry.captureException(error, { mechanism: { type: "react.router", handled: false } }) : undefined));
  return <ErrorScreen eventId={eventId} labels={labels()} onReload={() => window.location.reload()} report={report?.(eventId)} />;
}
